import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import {
  Wrapper,
  Text,
  TextFieldWrapper,
  TextInputLabel,
  SelectSearch,
} from "./styled";
import { MuiConfirmationModal } from "../MuiConfirmationModal";
import { InputField } from "../TextField";
import { CSSProperties } from "styled-components";
import { Stack, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

interface Props<T extends { id: string; name: string }> {
  title?: string;
  toolTipInfoText?: string;
  selectedValue?: T;
  onSelect?: (value: T) => void;
  onAddNew?: (value: T) => void;
  selectOptions?: T[];
  sx?: SxProps<Theme>;
  dataTestId?: string;
  wrapperStyle?: CSSProperties;
  popperZIndex?: number;
  modalZIndex?: number;
}

export function SelectSearchWithAddMui<T extends { id: string; name: string }>({
  title,
  toolTipInfoText,
  selectedValue,
  onSelect,
  onAddNew,
  selectOptions = [],
  wrapperStyle,
  sx,
  dataTestId,
  popperZIndex,
  modalZIndex,
}: Props<T>) {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleAutocompleteChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: unknown,
  ) => {
    const newValue = value as T | null;

    if (newValue) {
      if (newValue.id === "none-value") {
        setOpenModal(true);
      } else {
        onSelect?.(newValue);
      }
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setInputValue("");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleAddNewOption = async () => {
    if (!inputValue.trim()) {
      toastError(
        `${t(messages.Cant_Add_New_Unit())}. ${t(
          messages.Please_type_something(),
        )}.`,
      );
      return;
    }

    try {
      const newOption = { id: Date.now().toString(), name: inputValue } as T;
      onAddNew && (await onAddNew(newOption));
      toastSuccess(`${t(messages.Added_New_Unit_Successfully())}`);
      handleModalClose();
    } catch (error) {
      toastError(`${t(messages.Cant_Add_New_Unit_This_Unit_already_Exits())}`);
    }
  };

  return (
    <Wrapper
      wrapperStyle={wrapperStyle}
      data-testid="selectsearchwithaddmui-test-id"
    >
      {title && toolTipInfoText ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Text>{title}</Text>
          <Tooltip title={toolTipInfoText} arrow>
            <InfoIcon
              sx={{ fontSize: 16, color: "#9ca3afab", cursor: "help" }}
            />
          </Tooltip>
        </Stack>
      ) : (
        <Text>{title}</Text>
      )}
      <SelectSearch
        value={
          selectOptions.find((option) => option.id === selectedValue?.id) ||
          null
        }
        onChange={handleAutocompleteChange}
        options={
          onAddNew
            ? [
                ...selectOptions,
                {
                  id: "none-value",
                  name: `+ ${t(messages.Add_New())}`,
                } as T,
              ]
            : selectOptions
        }
        data-testid={dataTestId}
        getOptionLabel={(option) => (option as T).name}
        renderInput={(params) => <TextField {...params} />}
        ListboxProps={
          {
            "data-testid": `${dataTestId}-option`,
            sx: {
              "& .MuiAutocomplete-option": {
                backgroundColor: "white !important",
                borderRadius: "4px",
              },
              "& .MuiAutocomplete-option[aria-selected='true']": {
                backgroundColor: "#fd954fe0 !important",
                color: "white",
              },
              "& .MuiAutocomplete-option:hover": {
                backgroundColor: "#fd954f9c !important",
                color: "white",
              },
              "& .MuiAutocomplete-option.Mui-focused": {
                backgroundColor: "#fd954f9c !important",
                color: "white",
              },
            },
          } as any
        }
        sx={sx}
        disableClearable
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        componentsProps={{
          popper: {
            sx: popperZIndex ? { zIndex: popperZIndex } : undefined,
          },
        }}
      />
      {openModal && (
        <MuiConfirmationModal
          open={openModal}
          handleCancel={handleModalClose}
          handleConfirm={handleAddNewOption}
          title={t(messages.Add_Unit()) as string}
          dataTestId="add-unit-modal-test-id"
          sx={{ zIndex: modalZIndex ?? 1500 }}
          description={
            <TextFieldWrapper data-testid={`${dataTestId}-add-unit-wrapper`}>
              <TextInputLabel data-testid={`${dataTestId}-add-unit-label`}>
                {t(messages.Unit())}
              </TextInputLabel>
              <InputField
                data-testid={`${dataTestId}-add-unit-input`}
                type="text"
                placeholder={t(messages.Type_here()) as string}
                value={inputValue}
                onChange={handleInputChange}
              />
            </TextFieldWrapper>
          }
        />
      )}
    </Wrapper>
  );
}
